import { Link } from "react-router-dom";
import { ArrowRight, Layers, Lock, Search, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { homepageText, siteImage } from "@/lib/homepageContent";

const FEATURE_ICONS = [Layers, Search, ShieldCheck];

export default function Landing() {
  const { user, settings } = useAuth();
  const t = homepageText(settings);
  const HERO = siteImage(settings, "hero"), FIELDS = siteImage(settings, "fields");
  return (
    <div data-testid="landing-page">
      <section className="relative topo-bg grain text-white overflow-hidden">
        <img src={HERO} alt="Aerial view of surveyed farmland" data-testid="hero-image" className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-luminosity" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-28 lg:pt-28 lg:pb-36 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 fade-up">
            <p className="data-label text-emerald-400 mb-6" data-testid="hero-kicker">{t("hero_kicker")}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" data-testid="hero-title">
              {t("hero_title_before")}<span className="text-emerald-400">{t("hero_title_highlight")}</span>{t("hero_title_after")}
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl" data-testid="hero-subtitle">{t("hero_subtitle")}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button asChild data-testid="hero-primary-cta" size="lg" className="rounded-full h-12 px-7 bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold">
                <Link to={user ? "/map" : "/register"}>{user ? t("hero_cta_primary_user") : t("hero_cta_primary")} <ArrowRight size={18} className="ml-2" /></Link>
              </Button>
              <Button asChild data-testid="hero-secondary-cta" size="lg" variant="outline" className="rounded-full h-12 px-7 border-white/20 bg-white/5 text-white hover:bg-white/10">
                <Link to="/pricing">{t("hero_cta_secondary")}</Link>
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4 fade-up" style={{ animationDelay: "120ms" }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5" data-testid={`hero-stat-${i}`}><p className="font-mono text-2xl font-semibold text-emerald-300">{t(`stat${i}_value`)}</p><p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">{t(`stat${i}_label`)}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="data-label mb-4">{t("about_kicker")}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" data-testid="about-title">{t("about_title")}</h2>
          <p className="mt-4 text-muted-foreground">{t("about_text")}</p>
          <img src={FIELDS} alt="Cadastral farm fields from above" data-testid="fields-image" className="mt-8 rounded-2xl shadow-2xl aspect-[4/3] object-cover w-full" />
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5 content-start">
          {FEATURE_ICONS.map((Icon, i) => (
            <div key={i} className={`rounded-2xl border bg-card p-7 card-hover ${i === 0 ? "sm:col-span-2" : ""}`} data-testid={`feature-card-${i + 1}`}>
              <span className="w-11 h-11 rounded-xl grid place-items-center bg-emerald-50 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300"><Icon size={20} /></span>
              <h3 className="mt-5 text-xl font-semibold">{t(`feature${i + 1}_title`)}</h3>
              <p className="mt-2 text-muted-foreground">{t(`feature${i + 1}_text`)}</p>
            </div>
          ))}
          <div className="rounded-2xl p-7 text-white sm:col-span-2 relative overflow-hidden" style={{ background: "var(--terracotta)" }}>
            <Lock className="absolute -right-4 -bottom-4 opacity-20" size={140} />
            <p className="data-label text-white/80">{t("premium_kicker")}</p>
            <h3 className="mt-2 text-2xl font-bold" data-testid="premium-title">{t("premium_title")}</h3>
            <p className="mt-2 text-white/90 max-w-md">{t("premium_text")}</p>
            <Button asChild data-testid="landing-pricing-cta" className="mt-6 rounded-full bg-white text-slate-900 hover:bg-slate-100 font-semibold"><Link to="/pricing"><Sparkles size={16} className="mr-2" />{t("premium_cta")}</Link></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
